import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage, injectIntl, intlShape } from "react-intl";
import { Icon, Intent } from "@blueprintjs/core";
import { Button, ButtonSize } from "../../../buttons";
import { UserSelect } from "../../../users";
import {
  InputGroup,
  Input,
  Label,
  TextArea,
  DateInput,
  MultiInput
} from "../../../forms";
import messages from "../../Projects.messages";
import FormContainer from "../../../forms/formContainer/FormContainer";
import FormSectionContainer from "../../../forms/formSection/FormSectionContainer";
import FormSection from "../../../forms/formSection/FormSection";
import ButtonSection from "../../../forms/formSection/ButtonSection";
import { toLocalizedDateString } from "../../../../helpers/DateHelper";
import { ItemType } from "../..";
import { validateForm } from "./validation";
import { Flex } from "../../..";
import { DisplayStyle } from "../../../forms/styles/Styles";

class GoalForm extends React.Component {
  constructor(props) {
    super(props);

    const beginningDate =
      new Date(props.goal.beginningDate) < props.validationDates.minDate ||
      new Date(props.goal.beginningDate) > props.validationDates.maxDate
        ? props.validationDates.minDate
        : new Date(props.goal.beginningDate);

    const deadlineDate =
      new Date(props.goal.deadlineDate) > props.validationDates.maxDate ||
      new Date(props.goal.deadlineDate) < props.validationDates.minDate
        ? props.validationDates.maxDate
        : new Date(props.goal.deadlineDate);

    this.state = {
      data: {
        id: props.goal.id,
        name: props.goal.name,
        description: props.goal.description,
        measuringTool:
          props.goal.measuringTool === null ? "" : props.goal.measuringTool,
        manager: {
          id:
            props.goal.manager.id === 0
              ? props.users[0].id
              : props.goal.manager.id
        },
        priority: props.goal.priority,
        beginningDate,
        deadlineDate,
        creationDate: new Date(props.goal.creationDate),
        updateDate: new Date(props.goal.updateDate),
        progressPercentage: props.goal.progressPercentage,
        note: props.goal.note,
        benefits: props.goal.benefits,
        manualProgressPercentage: props.goal.manualProgressPercentage,
        isPrivate: props.goal.isPrivate
      },
      errors: {}
    };
  }

  managerSelected = user => {
    this.setState(prevState => ({
      data: {
        ...prevState.data,
        manager: {
          id: user.id
        }
      }
    }));
  };

  benefitValueUpdated = (value, index) => {
    const {
      data: { benefits: oldbenefits }
    } = this.state;
    let benefits = null;
    if (index === oldbenefits.length) {
      benefits = oldbenefits.concat({ name: value });
    } else if (value === "") {
      benefits = [
        ...oldbenefits.slice(0, index),
        ...oldbenefits.slice(index + 1)
      ];
    } else {
      benefits = [
        ...oldbenefits.slice(0, index),
        { name: value },
        ...oldbenefits.slice(index + 1)
      ];
    }

    this.setState(prevState => ({
      ...prevState,
      data: {
        ...prevState.data,
        benefits
      }
    }));
  };

  deadlineDateChanged = selectedDate => {
    this.setState(prevState => ({
      data: {
        ...prevState.data,
        deadlineDate: selectedDate
      }
    }));
  };

  beginningDateChanged = selectedDate => {
    this.setState(prevState => ({
      data: {
        ...prevState.data,
        beginningDate: selectedDate
      }
    }));
  };

  submit = event => {
    event.preventDefault();
    const {
      state: { data },
      props: { updateGoal, itemType }
    } = this;

    const errors = this.validate(data);
    if (Object.keys(errors).length === 0) {
      updateGoal(data, itemType);
    }

    this.setState({ errors });
  };

  validate = data => {
    const { intl } = this.props;
    return validateForm(data, ItemType.GOAL, intl);
  };

  onChange = ({ target: { value, name } }) => {
    const inputName = name;
    const inputValue = value;

    this.setState(prevState => ({
      data: {
        ...prevState.data,
        [inputName]: inputValue
      }
    }));
  };

  render() {
    const {
      state: {
        data: {
          name,
          description,
          measuringTool,
          manager,
          deadlineDate,
          priority,
          beginningDate,
          creationDate,
          progressPercentage,
          updateDate,
          note,
          benefits,
          manualProgressPercentage,
          isPrivate
        },
        errors
      },
      props: { users, intl, validationDates, loading, goal }
    } = this;

    const hasStrategies = goal.strategies && goal.strategies.length > 0;

    return (
      <FormContainer onSubmit={this.submit}>
        <FormSectionContainer>
          <FormSection
            itemType={ItemType.STRATEGY}
            title={intl.formatMessage(messages.dialog.generalInformations)}
          >
            <InputGroup error={errors.name}>
              <Label>
                <FormattedMessage {...messages.fields.name} />
              </Label>
              <Input name="name" value={name} onChange={this.onChange} />
            </InputGroup>

            <InputGroup error={errors.description}>
              <Label>Description</Label>
              <TextArea
                name="description"
                value={description}
                onChange={this.onChange}
              />
            </InputGroup>

            <InputGroup error={errors.measuringTool}>
              <Label>
                <FormattedMessage {...messages.fields.measuringTools} />
              </Label>
              <TextArea
                name="measuringTool"
                value={measuringTool}
                onChange={this.onChange}
              />
            </InputGroup>

            <InputGroup>
              <Label>
                <FormattedMessage {...messages.fields.manager} />
              </Label>
              <UserSelect
                users={users}
                activeUser={users[users.findIndex(x => x.id === manager.id)]}
                onUserSelected={this.managerSelected}
              />
            </InputGroup>

            <InputGroup error={errors.beginningDate}>
              <Label>
                <FormattedMessage {...messages.fields.beginningDate} />
              </Label>
              <DateInput
                onChange={this.beginningDateChanged}
                value={beginningDate}
                minDate={validationDates.minDate}
                maxDate={validationDates.maxDate}
              />
            </InputGroup>

            <InputGroup error={errors.deadlineDate}>
              <Label>
                <FormattedMessage {...messages.fields.deadlineDate} />
              </Label>
              <DateInput
                onChange={this.deadlineDateChanged}
                value={deadlineDate}
                minDate={validationDates.minDate}
                maxDate={validationDates.maxDate}
              />
            </InputGroup>

            <InputGroup error={errors.priority}>
              <Label>
                <FormattedMessage {...messages.fields.priority} />
              </Label>
              <Input
                name="priority"
                onChange={this.onChange}
                value={priority}
                type="number"
                min="1"
                max="4"
              />
            </InputGroup>
          </FormSection>
          <FormSection
            itemType={ItemType.STRATEGY}
            title={intl.formatMessage(messages.dialog.additionalInformations)}
          >
            <InputGroup>
              <Label>
                <FormattedMessage {...messages.fields.creationDate} />
              </Label>
              <Input
                name="creationDate"
                onChange={this.onChange}
                value={toLocalizedDateString(creationDate)}
                disabled
              />
            </InputGroup>

            <InputGroup>
              <Label>
                <FormattedMessage {...messages.fields.updateDate} />
              </Label>
              <Input
                name="updateDate"
                onChange={this.onChange}
                value={toLocalizedDateString(updateDate)}
                disabled
              />
            </InputGroup>

            <InputGroup>
              <Label>
                <FormattedMessage {...messages.fields.percentage} />
              </Label>
              {hasStrategies ? (
                <Input
                  name="progressPercentage"
                  onChange={this.onChange}
                  value={progressPercentage}
                  disabled
                />
              ) : (
                <Flex align="center">
                  <Input
                    min="0"
                    max="100"
                    name="manualProgressPercentage"
                    value={manualProgressPercentage}
                    onChange={this.onChange}
                  />
                  <Icon icon="edit" intent={Intent.PRIMARY} />
                </Flex>
              )}
            </InputGroup>

            <InputGroup displayStyle={DisplayStyle.INLINE}>
              <Label>
                <FormattedMessage {...messages.fields.isPrivate} />
              </Label>
              <Input
                type="checkbox"
                name="isPrivate"
                defaultChecked={isPrivate}
                onChange={e => {
                  this.onChange({
                    target: {
                      name: e.target.name,
                      value: e.target.checked
                    }
                  });
                }}
                value={isPrivate}
              />
            </InputGroup>

            <InputGroup error={errors.note}>
              <Label>
                <FormattedMessage {...messages.fields.notes} />
              </Label>
              <TextArea name="note" onChange={this.onChange} value={note} />
            </InputGroup>

            <InputGroup error={errors.benefits}>
              <Label>
                <FormattedMessage {...messages.fields.benefits} />
              </Label>
              <MultiInput
                values={benefits.map(x => x.name)}
                valueUpdated={this.benefitValueUpdated}
              />
            </InputGroup>
          </FormSection>
        </FormSectionContainer>
        <ButtonSection>
          <Button
            size={ButtonSize.MEDIUM}
            type="submit"
            itemType={ItemType.STRATEGY}
            disabled={loading}
          >
            <FormattedMessage {...messages.dialog.save} />
          </Button>
        </ButtonSection>
      </FormContainer>
    );
  }
}

GoalForm.defaultProps = {
  goal: {
    id: 0,
    name: "",
    description: "",
    measuringTool: "",
    manager: {
      id: 0
    },
    deadlineDate: new Date(),
    creationDate: new Date(),
    priority: 1,
    beginningDate: new Date(),
    progressPercentage: 0,
    updateDate: new Date(),
    note: "",
    benefits: [],
    manualProgressPercentage: 0,
    isPrivate: false
  }
};

GoalForm.propTypes = {
  goal: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    measuringTool: PropTypes.string,
    manager: PropTypes.shape(),
    creationDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    beginningDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    deadlineDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    updateDate: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date)
    ]),
    priority: PropTypes.number,
    progressPercentage: PropTypes.number,
    note: PropTypes.string,
    benefits: PropTypes.arrayOf(PropTypes.object),
    manualProgressPercentage: PropTypes.number,
    isPrivate: PropTypes.bool
  }),
  users: PropTypes.arrayOf(PropTypes.object).isRequired,
  updateGoal: PropTypes.func.isRequired,
  intl: intlShape.isRequired
};

export default injectIntl(GoalForm);
